import { useEffect, useState, useCallback } from "react";
import { ArrowRight, Search, CheckCircle } from "lucide-react";
import api from "../api/axios";
import toast from "react-hot-toast";

const STATUS_COLOR = {
  success: "text-emerald-400",
  pending: "text-amber-400",
  failed: "text-red-400",
};

export default function SendMoney() {
  const [tab, setTab] = useState("inapp"); // "inapp" | "bank"

  // ── In-app transfer state ──
  const [recipientEmail, setRecipientEmail] = useState("");
  const [resolvedUser, setResolvedUser] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [sendAmount, setSendAmount] = useState("");
  const [sendNote, setSendNote] = useState("");
  const [sending, setSending] = useState(false);
  const [inAppHistory, setInAppHistory] = useState([]);

  // ── Bank transfer state ──
  const [form, setForm] = useState({
    bankName: "",
    accountNumber: "",
    accountName: "",
    amount: "",
  });
  const [bankTransfers, setBankTransfers] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);

  // ── Load histories ──
  const fetchInAppHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/transfer/history");
      setInAppHistory(data);
    } catch {
      // ignore
    }
  }, []);

  const fetchBankTransfers = useCallback(async () => {
    try {
      const { data } = await api.get("/bank");
      setBankTransfers(data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchInAppHistory();
    fetchBankTransfers();
  }, [fetchInAppHistory, fetchBankTransfers]);

  // ── Debounced user lookup ──
  useEffect(() => {
    setResolvedUser(null);
    if (!recipientEmail.includes("@")) return;
    const t = setTimeout(async () => {
      setLookupLoading(true);
      try {
        const { data } = await api.get(`/transfer/lookup?email=${encodeURIComponent(recipientEmail.trim())}`);
        setResolvedUser(data);
      } catch {
        setResolvedUser(null);
      } finally {
        setLookupLoading(false);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [recipientEmail]);

  const handleSendInApp = async (e) => {
    e.preventDefault();
    if (!resolvedUser) return toast.error("Recipient not found");
    if (!sendAmount || Number(sendAmount) <= 0) return toast.error("Enter a valid amount");
    setSending(true);
    try {
      const { data } = await api.post("/transfer/send", {
        recipientEmail: recipientEmail.trim(),
        amount: Number(sendAmount),
        note: sendNote.trim(),
      });
      toast.success(data.message);
      setRecipientEmail("");
      setResolvedUser(null);
      setSendAmount("");
      setSendNote("");
      fetchInAppHistory();
    } catch (err) {
      toast.error(err.response?.data?.message || "Transfer failed");
    } finally {
      setSending(false);
    }
  };

  const handleBankChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    setBankLoading(true);
    try {
      const { data } = await api.post("/bank/request", form);
      toast.success(data.message || "Transfer submitted. Track its status below.");
      setForm({ bankName: "", accountNumber: "", accountName: "", amount: "" });
      fetchBankTransfers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Transfer failed");
    } finally {
      setBankLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 text-white">
      {/* Page header */}
      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <h1 className="text-2xl font-bold">Send Money</h1>
        <p className="mt-1 text-sm text-slate-400">Transfer funds to other MekPay users or bank accounts.</p>
      </section>

      {/* Tabs */}
      <div className="flex rounded-2xl border border-white/10 bg-slate-900/75 p-1 backdrop-blur-xl w-full max-w-xs">
        {[["inapp", "To MekPay User"], ["bank", "To Bank Account"]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex-1 rounded-xl py-2 text-sm font-semibold transition ${
              tab === key ? "bg-cyan-400 text-slate-900" : "text-slate-400 hover:text-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── In-app transfer ── */}
      {tab === "inapp" && (
        <>
          <form onSubmit={handleSendInApp} className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl space-y-4">
            <h2 className="font-semibold text-lg">Send to MekPay User</h2>

            <div>
              <label className="mb-1 block text-xs text-slate-400">Recipient email</label>
              <div className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-3 py-2.5">
                <Search size={16} className="text-slate-400 flex-shrink-0" />
                <input
                  type="email"
                  placeholder="user@example.com"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className="w-full bg-transparent text-white placeholder:text-slate-400 outline-none text-sm"
                />
                {lookupLoading && <span className="text-xs text-slate-400">…</span>}
                {resolvedUser && <CheckCircle size={16} className="text-emerald-400 flex-shrink-0" />}
              </div>
              {resolvedUser && (
                <p className="mt-1.5 text-sm text-emerald-400">✓ {resolvedUser.fullName}</p>
              )}
              {recipientEmail.includes("@") && !lookupLoading && !resolvedUser && (
                <p className="mt-1.5 text-sm text-red-400">User not found</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-xs text-slate-400">Amount (₦)</label>
              <input
                type="number"
                min="1"
                placeholder="0.00"
                value={sendAmount}
                onChange={(e) => setSendAmount(e.target.value)}
                className="w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-white placeholder:text-slate-400 outline-none"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs text-slate-400">Note (optional)</label>
              <input
                type="text"
                maxLength={200}
                placeholder="What's this for?"
                value={sendNote}
                onChange={(e) => setSendNote(e.target.value)}
                className="w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-white placeholder:text-slate-400 outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={sending || !resolvedUser}
              className="flex items-center gap-2 rounded-xl bg-cyan-400 px-6 py-2.5 font-semibold text-slate-900 transition hover:bg-cyan-300 disabled:opacity-50"
            >
              {sending ? "Sending…" : <><ArrowRight size={16} /> Send Money</>}
            </button>
          </form>

          <div className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
            <h2 className="text-lg font-semibold mb-4">Transfer History</h2>
            {inAppHistory.length === 0 ? (
              <p className="text-slate-400 text-sm">No in-app transfers yet.</p>
            ) : (
              <div className="space-y-3">
                {inAppHistory.map((t) => {
                  const dir = t.metadata?.direction;
                  return (
                    <div key={t._id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                      <div>
                        <p className="text-sm font-medium">
                          {dir === "out"
                            ? `→ ${t.metadata?.recipientName || t.metadata?.recipientEmail}`
                            : `← ${t.metadata?.senderName || t.metadata?.senderEmail}`}
                        </p>
                        {t.metadata?.note && <p className="text-xs text-slate-400 mt-0.5">{t.metadata.note}</p>}
                        <p className="text-xs text-slate-500 mt-0.5">{new Date(t.createdAt).toLocaleString()}</p>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${dir === "out" ? "text-red-400" : "text-emerald-400"}`}>
                          {dir === "out" ? "-" : "+"}₦{Number(t.amount).toLocaleString()}
                        </p>
                        <p className={`text-xs ${STATUS_COLOR[t.status] || "text-slate-400"}`}>{t.status}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Bank transfer ── */}
      {tab === "bank" && (
        <>
          <form onSubmit={handleBankSubmit} className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl space-y-4">
            <h2 className="font-semibold text-lg">Send to Bank Account</h2>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-[1.5rem] border border-white/10 bg-slate-950/50 p-4">
                <p className="text-sm uppercase tracking-[0.2em] text-slate-400">Transfer Method</p>
                <p className="mt-3 text-lg font-semibold text-white">Bank transfer</p>
              </div>
              <div className="rounded-[1.5rem] border border-white/10 bg-slate-950/50 p-4">
                <p className="text-sm uppercase tracking-[0.2em] text-slate-400">How it works</p>
                <p className="mt-3 text-sm leading-6 text-slate-300">
                  Send money to any bank account. Processing time can vary; you can track the status here.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[["bankName", "Bank Name"], ["accountNumber", "Account Number"], ["accountName", "Account Name"]].map(([name, placeholder]) => (
                <input
                  key={name}
                  type="text"
                  name={name}
                  placeholder={placeholder}
                  value={form[name]}
                  onChange={handleBankChange}
                  required
                  className="rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-white placeholder:text-slate-400 outline-none"
                />
              ))}
              <input
                type="number"
                name="amount"
                placeholder="Amount (₦)"
                value={form.amount}
                onChange={handleBankChange}
                required
                className="rounded-xl border border-white/15 bg-white/5 px-3 py-2.5 text-white placeholder:text-slate-400 outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={bankLoading}
              className="rounded-xl bg-accent px-6 py-2.5 font-semibold text-slate-950 transition hover:opacity-90 disabled:opacity-60"
            >
              {bankLoading ? "Sending…" : "Send"}
            </button>
          </form>

          <div className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl overflow-x-auto">
            <h2 className="text-lg font-semibold mb-4">Bank Transfer History</h2>
            {bankTransfers.length === 0 ? (
              <p className="text-slate-400 text-sm">No bank transfers yet.</p>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-white/10 text-slate-400 text-left">
                    {["Bank", "Account", "Amount", "Status", "Date"].map((h) => (
                      <th key={h} className="p-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bankTransfers.map((t, i) => (
                    <tr key={i} className="border-b border-white/5 text-slate-200">
                      <td className="p-3">{t.bankName}</td>
                      <td className="p-3">{t.accountNumber}</td>
                      <td className="p-3">₦{Number(t.amount).toLocaleString()}</td>
                      <td className={`p-3 font-medium ${STATUS_COLOR[t.status] || ""}`}>{t.status}</td>
                      <td className="p-3 text-slate-400">{new Date(t.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
